export const LEGAL_POLICY_DOCUMENTS = {
  privacy: {
    label: 'Politique de confidentialité',
    documents: {
      fr: {
        sourceFile: 'docs/legal/Privacy_Policy_FR.md',
        content: `# Politique de confidentialité WorkflowPro

**Statut:** Approuvé

**Version:** 1.0

**Propriétaire:** Fondateur

**Langue:** Français

**Dernière mise à jour:** 2026-07-15

# 1. Introduction

WorkflowPro ("nous", "notre" ou "nos") respecte votre vie privée et s'engage à protéger les informations personnelles.

Cette Politique de confidentialité explique comment WorkflowPro collecte, utilise, stocke et protège les données personnelles lorsque vous utilisez la plateforme.

# 2. Champ d'application

Cette Politique de confidentialité s'applique:

* aux recruteurs utilisant WorkflowPro;
* aux informations candidat téléversées par des utilisateurs autorisés;
* aux visiteurs du site web WorkflowPro;
* aux prospects qui contactent WorkflowPro.

# 3. Informations que nous collectons

Selon la manière dont WorkflowPro est utilisé, nous pouvons collecter:

### Informations recruteur

* nom;
* adresse email;
* informations de compte;
* informations d'abonnement;
* informations d'utilisation.

### Informations candidat

Les utilisateurs autorisés peuvent téléverser des CV contenant des informations personnelles, notamment:

* nom;
* coordonnées;
* historique professionnel;
* formation;
* qualifications;
* compétences;
* autres informations contenues dans le CV téléversé.

# 4. Comment nous utilisons les informations

Les informations sont traitées pour:

* fournir les services WorkflowPro;
* authentifier les utilisateurs;
* analyser les CV téléversés;
* générer des résumés orientés recruteur;
* comparer les candidats aux exigences du poste;
* gérer les abonnements;
* améliorer la fiabilité de la plateforme;
* fournir le support client.

WorkflowPro assiste les recruteurs mais ne prend pas de décisions de recrutement.

# 5. Traitement IA

Les CV téléversés peuvent être traités à l'aide de l'intelligence artificielle afin de générer des résumés structurés et des analyses de candidats.

Les résultats IA sont destinés à soutenir les recruteurs et ne doivent pas remplacer le jugement professionnel.

# 6. Base légale

Lorsque cela s'applique, les informations personnelles sont traitées sur des bases légales appropriées, notamment:

* l'exécution d'un contrat;
* les intérêts commerciaux légitimes;
* les obligations légales;
* le consentement de l'utilisateur lorsque requis.

# 7. Partage des données

WorkflowPro ne vend pas les informations personnelles.

Les informations peuvent être partagées uniquement avec les prestataires nécessaires au fonctionnement de la plateforme, notamment les fournisseurs d'infrastructure cloud et de paiement.

# 8. Sécurité des données

WorkflowPro utilise des mesures techniques et organisationnelles appropriées pour protéger les informations personnelles, notamment:

* l'accès authentifié;
* les communications chiffrées;
* les contrôles d'accès;
* le monitoring;
* l'infrastructure cloud managée.

# 9. Conservation des données

Les informations personnelles sont conservées uniquement aussi longtemps que nécessaire pour fournir le service, satisfaire aux obligations légales ou résoudre des exigences opérationnelles légitimes.

# 10. Vos droits

Lorsque cela s'applique, les personnes peuvent disposer de droits comprenant:

* l'accès aux informations personnelles;
* la correction des informations inexactes;
* la suppression des informations personnelles;
* la limitation du traitement;
* l'opposition à certaines activités de traitement;
* la portabilité des données.

# 11. Cookies

WorkflowPro peut utiliser des cookies ou technologies similaires nécessaires à l'authentification, à la sécurité et au fonctionnement de la plateforme.

Une Politique relative aux cookies distincte fournit des informations supplémentaires.

# 12. Traitement international

WorkflowPro peut traiter les informations à l'aide d'une infrastructure cloud située dans des juridictions appropriées à la fourniture du service.

# 13. Modifications

Cette Politique de confidentialité peut être mise à jour périodiquement.

Les changements matériels seront reflétés par la mise à jour de la version et de la date de publication.

# 14. Contact

Les questions concernant cette Politique de confidentialité peuvent être adressées à WorkflowPro via les coordonnées publiées sur le site officiel.

# Résumé

WorkflowPro s'engage à protéger les informations personnelles, à traiter les données de manière responsable, à soutenir la prise de décision des recruteurs et à maintenir la transparence sur la manière dont les informations personnelles sont traitées.

---

## Informations sur le document

**Document:** Politique de confidentialité WorkflowPro

**Version:** 1.0

**Statut:** Approuvé

**Langue:** Français

**Propriétaire:** Fondateur

**Dernière mise à jour:** 2026-07-15

---`,
      },
      en: {
        sourceFile: 'docs/legal/Privacy_Policy_EN.md',
        content: `# WorkflowPro Privacy Policy

**Status:** Approved

**Version:** 1.0

**Owner:** Founder

**Language:** English (Master)

**Last Updated:** 2026-07-15

# 1. Introduction

WorkflowPro ("we", "our", or "us") respects your privacy and is committed to protecting personal information.

This Privacy Policy explains how WorkflowPro collects, uses, stores, and protects personal data when you use the platform.

# 2. Scope

This Privacy Policy applies to:

* recruiters using WorkflowPro;
* candidate information uploaded by authorised users;
* visitors to the WorkflowPro website;
* prospective customers contacting WorkflowPro.

# 3. Information We Collect

Depending on how WorkflowPro is used, we may collect:

### Recruiter Information

* name;
* email address;
* account information;
* subscription information;
* usage information.

### Candidate Information

Authorised users may upload CVs containing personal information including:

* name;
* contact details;
* employment history;
* education;
* qualifications;
* skills;
* other information contained within the uploaded CV.

# 4. How We Use Information

Information is processed to:

* provide WorkflowPro services;
* authenticate users;
* analyse uploaded CVs;
* generate recruiter-focused summaries;
* compare candidates with job requirements;
* manage subscriptions;
* improve platform reliability;
* provide customer support.

WorkflowPro assists recruiters but does not make hiring decisions.

# 5. AI Processing

Uploaded CVs may be processed using artificial intelligence to generate structured summaries and candidate analyses.

AI output is intended to support recruiters and should not replace professional judgement.

# 6. Legal Basis

Where applicable, personal information is processed on appropriate legal grounds, including:

* performance of a contract;
* legitimate business interests;
* legal obligations;
* user consent where required.

# 7. Data Sharing

WorkflowPro does not sell personal information.

Information may be shared only with service providers necessary to operate the platform, including cloud infrastructure and payment providers.

# 8. Data Security

WorkflowPro uses appropriate technical and organisational measures to protect personal information, including:

* authenticated access;
* encrypted communications;
* access controls;
* monitoring;
* managed cloud infrastructure.

# 9. Data Retention

Personal information is retained only for as long as necessary to provide the service, satisfy legal obligations, or resolve legitimate operational requirements.

# 10. Your Rights

Where applicable, individuals may have rights including:

* access to personal information;
* correction of inaccurate information;
* deletion of personal information;
* restriction of processing;
* objection to certain processing activities;
* data portability.

# 11. Cookies

WorkflowPro may use cookies or similar technologies required for authentication, security, and operation of the platform.

A separate Cookie Policy provides additional information.

# 12. International Processing

WorkflowPro may process information using cloud infrastructure located in jurisdictions appropriate for service delivery.

# 13. Changes

This Privacy Policy may be updated from time to time.

Material changes will be reflected by updating the version and publication date.

# 14. Contact

Questions regarding this Privacy Policy may be directed to WorkflowPro using the contact information published on the official website.

# Summary

WorkflowPro is committed to protecting personal information, processing data responsibly, supporting recruiter decision-making, and maintaining transparency regarding how personal information is handled.

---

## Document Information

**Document:** WorkflowPro Privacy Policy

**Version:** 1.0

**Status:** Approved

**Language:** English (Master)

**Owner:** Founder

**Last Updated:** 2026-07-15

---`,
      },
    },
  },
  terms: {
    label: "Conditions d'utilisation",
    documents: {
      fr: {
        sourceFile: 'docs/legal/Terms_of_Service_FR.md',
        content: `# Conditions d'utilisation de WorkflowPro

**Statut:** Approuvé

**Version:** 1.1

**Propriétaire:** Fondateur

**Langue:** Français

**Dernière mise à jour:** 2026-08-31

# 1. Introduction

Les présentes Conditions d'utilisation régissent l'utilisation de WorkflowPro.

En créant un compte ou en utilisant WorkflowPro, vous acceptez ces Conditions.

Si vous ne les acceptez pas, vous ne devez pas utiliser la plateforme.

# 2. Description du service

WorkflowPro est une plateforme de recrutement assistée par IA conçue pour aider les recruteurs à organiser, analyser et comparer les CV des candidats.

WorkflowPro fournit uniquement des outils d'aide à la décision.

Les décisions de recrutement restent sous la seule responsabilité du recruteur.

# 3. Éligibilité

Les utilisateurs doivent:

* fournir des informations exactes;
* maintenir la sécurité du compte;
* utiliser la plateforme légalement;
* disposer de l'autorité nécessaire pour téléverser des informations candidat.

# 4. Responsabilités des utilisateurs

Les utilisateurs acceptent de:

* protéger leurs identifiants de connexion;
* téléverser uniquement des informations qu'ils sont autorisés à traiter;
* respecter les lois applicables en matière de confidentialité et d'emploi;
* utiliser WorkflowPro de manière responsable.

Les utilisateurs restent responsables des décisions de recrutement.

# 5. Utilisation acceptable

Les utilisateurs ne doivent pas:

* tenter un accès non autorisé;
* perturber le fonctionnement de la plateforme;
* téléverser du contenu illicite;
* détourner l'analyse générée par IA;
* utiliser WorkflowPro à des fins illégales.

# 6. Analyse assistée par IA

WorkflowPro utilise l'intelligence artificielle pour assister les recruteurs.

Les résultats générés par IA:

* peuvent contenir des inexactitudes;
* sont destinés à l'aide à la décision;
* doivent toujours être revus par un recruteur humain.

WorkflowPro ne garantit pas de recommandations de recrutement.

# 7. Comptes

Les utilisateurs sont responsables de:

* maintenir la sécurité du compte;
* protéger les mots de passe;
* signaler tout accès non autorisé suspecté.

# 8. Abonnements

Les fonctionnalités d'abonnement peuvent nécessiter un paiement.

Les abonnements payants sont gérés via le parcours de paiement et de facturation WorkflowPro.

La disponibilité des abonnements commerciaux, les prix et les informations de facturation sont présentés au client dans la plateforme ou dans les documents de tarification WorkflowPro publiés.

# 9. Disponibilité

WorkflowPro vise à fournir un service fiable, mais ne garantit pas une disponibilité ininterrompue.

La maintenance, les mises à jour et les événements imprévus peuvent affecter temporairement le service.

# 10. Propriété intellectuelle

WorkflowPro, son logiciel, sa marque et sa documentation restent la propriété de leurs détenteurs respectifs, sauf indication contraire.

Les utilisateurs conservent la propriété des informations qu'ils téléversent.

# 11. Limitation de responsabilité

WorkflowPro est fourni sur une base "selon disponibilité".

Dans toute la mesure permise par la loi, WorkflowPro n'est pas responsable des pertes indirectes ou consécutives résultant de l'utilisation de la plateforme.

# 12. Résiliation

WorkflowPro peut suspendre ou résilier les comptes qui violent ces Conditions ou la loi applicable.

Les utilisateurs peuvent cesser d'utiliser la plateforme à tout moment.

# 13. Modifications

Ces Conditions peuvent être mises à jour périodiquement.

Les changements matériels seront reflétés par la mise à jour de la version et de la date de publication.

# 14. Droit applicable

Ces Conditions sont régies par les lois applicables à la juridiction dans laquelle WorkflowPro opère, sauf exigence contraire de la loi.

# 15. Contact

Les questions concernant ces Conditions peuvent être adressées via les coordonnées publiées sur le site web WorkflowPro.

# Résumé

Ces Conditions établissent les responsabilités de WorkflowPro et de ses utilisateurs tout en garantissant que l'IA reste un outil d'aide à la décision sous responsabilité humaine.

---

## Informations sur le document

**Document:** Conditions d'utilisation de WorkflowPro

**Version:** 1.1

**Statut:** Approuvé

**Langue:** Français

**Propriétaire:** Fondateur

**Dernière mise à jour:** 2026-08-31

---`,
      },
      en: {
        sourceFile: 'docs/legal/Terms_of_Service_EN.md',
        content: `# WorkflowPro Terms of Service

**Status:** Approved

**Version:** 1.1

**Owner:** Founder

**Language:** English (Master)

**Last Updated:** 2026-08-31

# 1. Introduction

These Terms of Service govern the use of WorkflowPro.

By creating an account or using WorkflowPro, you agree to these Terms.

If you do not agree, you must not use the platform.

# 2. Service Description

WorkflowPro is an AI-assisted recruitment platform designed to help recruiters organise, analyse and compare candidate CVs.

WorkflowPro provides decision-support tools only.

Hiring decisions remain the sole responsibility of the recruiter.

# 3. Eligibility

Users must:

* provide accurate information;
* maintain account security;
* use the platform lawfully;
* have authority to upload candidate information.

# 4. User Responsibilities

Users agree to:

* protect login credentials;
* upload only information they are authorised to process;
* comply with applicable privacy and employment laws;
* use WorkflowPro responsibly.

Users remain responsible for recruitment decisions.

# 5. Acceptable Use

Users must not:

* attempt unauthorised access;
* interfere with platform operation;
* upload unlawful content;
* misuse AI-generated analysis;
* use WorkflowPro for illegal purposes.

# 6. AI-Assisted Analysis

WorkflowPro uses artificial intelligence to assist recruiters.

AI-generated outputs:

* may contain inaccuracies;
* are intended as decision support;
* should always be reviewed by a human recruiter.

WorkflowPro does not guarantee hiring recommendations.

# 7. Accounts

Users are responsible for:

* maintaining account security;
* safeguarding passwords;
* reporting suspected unauthorised access.

# 8. Subscriptions

Subscription features may require payment.

Paid subscriptions are handled through the WorkflowPro checkout and billing flow.

Commercial subscription availability, pricing, and billing details are presented to the customer in the platform or in published WorkflowPro pricing material.

# 9. Availability

WorkflowPro aims to provide reliable service but does not guarantee uninterrupted availability.

Maintenance, updates and unforeseen events may temporarily affect service.

# 10. Intellectual Property

WorkflowPro, its software, branding and documentation remain the property of their respective owners unless otherwise stated.

Users retain ownership of the information they upload.

# 11. Limitation of Liability

WorkflowPro is provided on an "as available" basis.

To the maximum extent permitted by law, WorkflowPro is not responsible for indirect or consequential losses arising from use of the platform.

# 12. Termination

WorkflowPro may suspend or terminate accounts that violate these Terms or applicable law.

Users may stop using the platform at any time.

# 13. Changes

These Terms may be updated periodically.

Material changes will be reflected by updating the version and publication date.

# 14. Governing Law

These Terms shall be governed by the laws applicable to the jurisdiction in which WorkflowPro operates, unless otherwise required by law.

# 15. Contact

Questions regarding these Terms may be directed using the contact information published on the WorkflowPro website.

# Summary

These Terms establish the responsibilities of WorkflowPro and its users while ensuring that AI remains a decision-support tool under human responsibility.

---

## Document Information

**Document:** WorkflowPro Terms of Service

**Version:** 1.1

**Status:** Approved

**Language:** English (Master)

**Owner:** Founder

**Last Updated:** 2026-08-31

---`,
      },
    },
  },
  cookies: {
    label: 'Politique relative aux cookies',
    documents: {
      fr: {
        sourceFile: 'docs/legal/Cookie_Policy_FR.md',
        content: `# Politique relative aux cookies WorkflowPro

**Statut:** Approuvé

**Version:** 1.1

**Propriétaire:** Fondateur

**Langue:** Français

**Dernière mise à jour:** 2026-08-31

# 1. Introduction

Cette Politique relative aux cookies explique comment WorkflowPro utilise les cookies et technologies similaires lorsque vous accédez au site web et à la plateforme WorkflowPro.

Elle doit être lue conjointement avec la Politique de confidentialité WorkflowPro.

# 2. Que sont les cookies?

Les cookies sont de petits fichiers texte stockés sur votre appareil par votre navigateur web.

Ils aident les sites web à mémoriser des informations telles que les sessions de connexion, les préférences et les paramètres de sécurité.

# 3. Pourquoi nous utilisons des cookies

WorkflowPro utilise des cookies pour:

* authentifier les utilisateurs;
* maintenir des sessions sécurisées;
* mémoriser les préférences utilisateur;
* améliorer la fiabilité de la plateforme;
* soutenir le fonctionnement de la plateforme;
* protéger la plateforme contre les abus.

# 4. Types de cookies

## Cookies essentiels

Les cookies essentiels sont nécessaires au bon fonctionnement de la plateforme.

Ces cookies prennent en charge:

* l'authentification;
* la sécurité;
* la gestion des sessions;
* les fonctionnalités de la plateforme.

Les cookies essentiels ne peuvent généralement pas être désactivés sans affecter le fonctionnement de la plateforme.

## Cookies fonctionnels

Les cookies fonctionnels améliorent l'expérience utilisateur en mémorisant les préférences lorsque cela est approprié.

## Cookies d'analyse web et marketing

Pour le lancement initial contrôlé en France, WorkflowPro n'utilise pas de services tiers d'analyse web ou marketing ni de cookies analytiques.

Cela n'empêche pas WorkflowPro d'utiliser les cookies essentiels et les technologies opérationnelles nécessaires à l'authentification, à la sécurité, à la gestion des sessions, à la fiabilité de la plateforme et à la prévention des abus.

# 5. Services tiers

WorkflowPro peut s'appuyer sur des services tiers de confiance qui utilisent des cookies ou technologies similaires dans le cadre de leur fonctionnement normal.

Les exemples peuvent inclure:

* fournisseurs d'authentification;
* fournisseurs de paiement;
* services d'infrastructure cloud.

WorkflowPro ne contrôle pas les politiques de cookies des tiers.

# 6. Gestion des cookies

La plupart des navigateurs permettent aux utilisateurs de:

* consulter les cookies stockés;
* supprimer les cookies;
* bloquer les cookies;
* configurer les préférences de cookies.

La désactivation de certains cookies peut affecter les fonctionnalités de la plateforme.

# 7. Modifications

Cette Politique relative aux cookies peut être mise à jour périodiquement.

Les changements matériels seront reflétés par la mise à jour de la version et de la date de publication.

# 8. Contact

Les questions concernant cette Politique relative aux cookies peuvent être adressées via les coordonnées publiées sur le site web WorkflowPro.

# Résumé

WorkflowPro utilise les cookies principalement pour fournir une authentification sécurisée, maintenir les fonctionnalités de la plateforme, améliorer la fiabilité et soutenir une expérience utilisateur cohérente.

---

## Informations sur le document

**Document:** Politique relative aux cookies WorkflowPro

**Version:** 1.1

**Statut:** Approuvé

**Langue:** Français

**Propriétaire:** Fondateur

**Dernière mise à jour:** 2026-08-31

---`,
      },
      en: {
        sourceFile: 'docs/legal/Cookie_Policy_EN.md',
        content: `# WorkflowPro Cookie Policy

**Status:** Approved

**Version:** 1.1

**Owner:** Founder

**Language:** English (Master)

**Last Updated:** 2026-08-31

# 1. Introduction

This Cookie Policy explains how WorkflowPro uses cookies and similar technologies when you access the WorkflowPro website and platform.

It should be read together with the WorkflowPro Privacy Policy.

# 2. What Are Cookies?

Cookies are small text files stored on your device by your web browser.

They help websites remember information such as login sessions, preferences, and security settings.

# 3. Why We Use Cookies

WorkflowPro uses cookies to:

* authenticate users;
* maintain secure sessions;
* remember user preferences;
* improve platform reliability;
* support platform operation;
* protect the platform against abuse.

# 4. Types of Cookies

## Essential Cookies

Essential cookies are required for the platform to operate correctly.

These cookies support:

* authentication;
* security;
* session management;
* platform functionality.

Essential cookies cannot normally be disabled without affecting platform operation.

## Functional Cookies

Functional cookies improve the user experience by remembering preferences where appropriate.

## Website and Marketing Analytics Cookies

For the initial controlled France launch, WorkflowPro does not use third-party website or marketing analytics services or analytics cookies.

This does not prevent WorkflowPro from using essential cookies and operational technologies required for authentication, security, session management, platform reliability, and abuse prevention.

# 5. Third-Party Services

WorkflowPro may rely on trusted third-party services that use cookies or similar technologies as part of their normal operation.

Examples may include:

* authentication providers;
* payment providers;
* cloud infrastructure services.

WorkflowPro does not control third-party cookie policies.

# 6. Managing Cookies

Most browsers allow users to:

* view stored cookies;
* delete cookies;
* block cookies;
* configure cookie preferences.

Disabling certain cookies may affect platform functionality.

# 7. Changes

This Cookie Policy may be updated from time to time.

Material changes will be reflected by updating the version and publication date.

# 8. Contact

Questions regarding this Cookie Policy may be directed using the contact information published on the WorkflowPro website.

# Summary

WorkflowPro uses cookies primarily to provide secure authentication, maintain platform functionality, improve reliability, and support a consistent user experience.

---

## Document Information

**Document:** WorkflowPro Cookie Policy

**Version:** 1.1

**Status:** Approved

**Language:** English (Master)

**Owner:** Founder

**Last Updated:** 2026-08-31

---`,
      },
    },
  },
}
